import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Multi-select searchable filter dropdown.
 * @param {{ label, items: [{value, label}], values: string[], onChange: (string[]) => void }} props
 */
export default function SearchableFilter({ label, items, values = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return items.slice(0, 20);
    return items.filter(i => i.label.toLowerCase().includes(search.toLowerCase())).slice(0, 20);
  }, [items, search]);

  const toggle = (val) => {
    const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val];
    onChange(next);
  };

  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const selectedLabels = values.map(v => items.find(i => i.value === v)?.label).filter(Boolean);
  const displayText = selectedLabels.length === 0 ? "All"
    : selectedLabels.length === 1 ? selectedLabels[0]
    : `${selectedLabels.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 px-2.5 py-[5px] text-[12px] border border-[#E0E0DE] rounded-[4px] hover:bg-[#E8E8E6] transition-colors min-w-[100px] max-w-[200px]" data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`}>
          <span className="text-[#6B6B6B] shrink-0">{label}:</span>
          <span className="text-[#37352F] truncate font-medium">{displayText}</span>
          {values.length > 0 ? (
            <X className="w-3 h-3 text-[#6B6B6B] hover:text-[#EB5757] shrink-0 ml-auto" onClick={clear} />
          ) : (
            <ChevronDown className="w-3 h-3 text-[#6B6B6B] shrink-0 ml-auto" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-white border-[#E0E0DE] shadow-md p-0 w-[220px]" align="start">
        <div className="p-2 border-b border-[#E8E8E6]">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-[#F7F6F3] rounded-[4px]">
            <Search className="w-3 h-3 text-[#A3A3A0]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search...`}
              className="flex-1 bg-transparent border-none outline-none text-[13px] text-[#37352F] placeholder:text-[#A3A3A0]" autoFocus />
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto py-1">
          {values.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-[13px] text-[#6B6B6B] hover:bg-[#F7F6F3]">Clear all</button>
          )}
          {filtered.map(item => (
            <button key={item.value} onClick={() => toggle(item.value)}
              className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-[#F7F6F3] transition-colors flex items-center gap-2">
              <Checkbox checked={values.includes(item.value)} className="h-3.5 w-3.5 rounded-[2px] border-[#B8B6B0] data-[state=checked]:bg-[#2383E2] data-[state=checked]:border-[#2383E2]" />
              <span className="truncate">{item.label}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-2 text-[12px] text-[#A3A3A0] italic">No results</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
